import { useContext, useMemo, useState } from "react";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";

import MUIAlert from "@mui/material/Alert";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUIButton from "@mui/material/Button";
import MUIDialog from "@mui/material/Dialog";
import MUIDialogActions from "@mui/material/DialogActions";
import MUIDialogContent from "@mui/material/DialogContent";
import MUIDialogTitle from "@mui/material/DialogTitle";
import MUILoadingButton from "@mui/lab/LoadingButton";
import MUIStack from "@mui/material/Stack";
import MUITextField from "@mui/material/TextField";
import MUITypography from "@mui/material/Typography";
import * as MUIIcons from "@mui/icons-material";

import { ChartContext } from "../../Context/ChartContext/ChartContext";
import MainContext from "../../Context/MainContext/MainContext";
import api from "../../Seating/Security/api";
import OrderActivity from "../../Utility/Crud/OrderActivity";
import OrderActivityType from "../../Utility/Crud/OrderActivityType";
import OrderStatusReason from "../../Utility/Crud/OrderStatusReason";
import User from "../../Utility/Crud/User";

/**
 * Allows an authorized user to change the parent order for the current order.
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open.
 * @param {Function} props.onClose - Closes the dialog.
 * @returns {JSX.Element}
 */
export default function DialogChangeParentOrder({ open, onClose }) {
    const chartContext = useContext(ChartContext);
    const mainContext = useContext(MainContext);
    const [selectedParentOrder, setSelectedParentOrder] = useState(null);
    const [changingParentOrder, setChangingParentOrder] = useState(false);

    const orderActivity = chartContext.originalChart.orderActivity[chartContext.orderId];
    const serviceOrderReason = OrderActivity.getServiceOrderReason(
        orderActivity,
        chartContext.originalChart
    );
    const currentParentOrderId = OrderActivity.getParentOrder(
        serviceOrderReason?.previousOrderId,
        chartContext.originalChart
    )?.id || serviceOrderReason?.previousOrderId;

    const parentOrderOptions = useMemo(() => {
        const activityMap = chartContext.originalChart.activity || {};

        return OrderActivity.sort(
            Object.values(chartContext.originalChart.orderActivity || {})
                .filter((order) => {
                    if (activityMap[order.id]?.type !== "Order" || order.id === orderActivity.id) {
                        return false;
                    }

                    if (order.orderType !== "Accessory After Sale") {
                        return true;
                    }

                    const serviceReason = OrderActivity.getServiceOrderReason(
                        order,
                        chartContext.originalChart
                    );

                    return !serviceReason?.previousOrderId;
                })
        ).map((order) => {
            const orderStatusReason = mainContext.orderStatusReasonsIndexed?.[order.orderStatusReasonId];
            const statusLabel = OrderStatusReason.getDisplayName(orderStatusReason)
                || mainContext.orderStatusesIndexed?.[order.status]?.name
                || "Unknown Status";
            let title;

            if (order.orderType === "New") {
                const primaryOrderProduct = OrderActivity.getPrimaryProduct(
                    OrderActivity.getProducts(order, chartContext.originalChart)
                );
                title = OrderActivity.getProductDisplayName(
                    primaryOrderProduct,
                    mainContext.baseUnitsIndexed,
                    mainContext.vendorsIndexed,
                    mainContext.productSubTypesIndexed
                ) || "New Order";
            } else {
                title = `${order.orderType} Order`;
            }

            return {
                value: order.id,
                inputLabel: `#${order.id} • ${title}`,
                searchLabel: `${title} ${order.orderType || ""} #${order.id} ${statusLabel}`,
                orderType: order.orderType,
                title,
                metaLine: `Order #${order.id} • ${statusLabel}`
            };
        });
    }, [chartContext.originalChart, mainContext, orderActivity.id]);

    const currentParentOrderOption = parentOrderOptions.find(
        (option) => option.value === currentParentOrderId
    );
    const displayedParentOrder = selectedParentOrder || currentParentOrderOption || null;

    const handleClose = () => {
        setSelectedParentOrder(null);
        onClose();
    };

    const handleChangeParentOrder = async () => {
        setChangingParentOrder(true);
        try {
            const serviceOrderReasonId = serviceOrderReason?.id || chartContext.getNegativeId();
            const activity = chartContext.originalChart.activity[orderActivity.id];
            const salesRep = mainContext.usersIndexed[activity.ownerId];

            await api.send("POST", "/chart/update", {
                patientId: parseInt(chartContext.patientId),
                updateData: {
                    serviceOrderReason: {
                        [serviceOrderReasonId]: {
                            id: serviceOrderReasonId,
                            previousOrderId: displayedParentOrder.value,
                            ...(!serviceOrderReason && {
                                currentOrderId: orderActivity.id,
                                name: orderActivity.patientName,
                                technician: orderActivity.technician ? orderActivity.technician.name : "No Technician",
                                salesRep: salesRep ? User.getDisplayName(salesRep) : "No Sales Rep",
                                atp: orderActivity.atp ? orderActivity.atp.name : "No ATP",
                                serviceReason: ""
                            })
                        }
                    }
                },
                returnChart: false
            }, 0);
            await chartContext.reloadChartData();
            handleClose();
            NotistackEnqueueSnackbar("Parent order changed", { variant: "success" });
        } catch (error) {
            console.error(error);
            NotistackEnqueueSnackbar("Error changing parent order", { variant: "error" });
        } finally {
            setChangingParentOrder(false);
        }
    };

    return (
        <MUIDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <MUIDialogTitle>Change Parent Order</MUIDialogTitle>
            <MUIDialogContent>
                <MUIAlert severity={chartContext.hasPendingChanges ? "error" : "warning"} sx={{ mt: 1, mb: 2 }}>
                    {chartContext.hasPendingChanges
                        ? "Save or discard all pending chart changes before changing the parent order."
                        : "This will link this order to a new parent order and reload the chart."}
                </MUIAlert>
                <MUIAutocomplete
                    options={parentOrderOptions}
                    value={displayedParentOrder}
                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                    getOptionLabel={(option) => option?.inputLabel || ""}
                    filterOptions={(options, state) => {
                        const query = state.inputValue.toLowerCase();
                        return options.filter((option) => option.searchLabel.toLowerCase().includes(query));
                    }}
                    onChange={(_, value) => setSelectedParentOrder(value)}
                    disableClearable
                    renderOption={(props, option) => {
                        const IconComponent = MUIIcons[OrderActivityType.getIconName(option.orderType)] || MUIIcons.Workspaces;
                        return (
                            <li {...props}>
                                <MUIStack direction="row" spacing={1} alignItems="flex-start" sx={{ width: "100%" }}>
                                    <IconComponent fontSize="small" sx={{ mt: 0.25 }} />
                                    <MUIStack spacing={0} sx={{ flexGrow: 1, minWidth: 0 }}>
                                        <MUITypography variant="body2" noWrap>{option.title}</MUITypography>
                                        <MUITypography variant="caption" color="text.secondary" noWrap>
                                            {option.metaLine}
                                        </MUITypography>
                                    </MUIStack>
                                </MUIStack>
                            </li>
                        );
                    }}
                    renderInput={(params) => (
                        <MUITextField
                            {...params}
                            label="Parent Order"
                            fullWidth
                            size="small"
                            variant="standard"
                            InputLabelProps={{ shrink: true }}
                        />
                    )}
                />
            </MUIDialogContent>
            <MUIDialogActions>
                <MUIButton onClick={handleClose}>Cancel</MUIButton>
                <MUILoadingButton
                    variant="contained"
                    loading={changingParentOrder || chartContext.isUpdating}
                    onClick={handleChangeParentOrder}
                    disabled={
                        chartContext.hasPendingChanges ||
                        changingParentOrder ||
                        !displayedParentOrder ||
                        displayedParentOrder.value === currentParentOrderId
                    }
                >
                    Change Parent
                </MUILoadingButton>
            </MUIDialogActions>
        </MUIDialog>
    );
}
